import React, { useState, useEffect, useMemo, createContext, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, ShoppingCart, Package, Truck, MapPin, 
  UtensilsCrossed, ChefHat, Users, DollarSign, FileText,
  Wallet, BarChart3, Settings, Menu, X, Warehouse, HeartHandshake, Shield, PieChart, Store, Activity,
  ChevronDown, ChevronRight, Receipt, Banknote, Printer, Bike, CheckCircle2, AlertCircle, Route,
  QrCode, CreditCard, Tag, Coffee, Zap, MessageSquare, Smartphone, ExternalLink, Bot, Webhook, 
  ScrollText, GitBranch, Cog, Inbox, Building2, Lock, Flame
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { usePermissions, ROUTE_PERMISSIONS } from '@/hooks/usePermissions';
import { cn } from '@/lib/utils';
import api from '@/services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MobileMenuContext = createContext({ open: false, setOpen: () => {} });

const menuItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/' },
  { 
    icon: ShoppingCart, 
    label: 'PDV', 
    path: '/pdv',
    submenu: [
      { icon: Coffee, label: 'Mesa (Salão)', path: '/salao' },
      { icon: Zap, label: 'Venda Rápida', path: '/venda-rapida' },
    ]
  },
  { 
    icon: Truck, 
    label: 'Delivery', 
    path: '/delivery',
    submenu: [
      { icon: UtensilsCrossed, label: 'Pedidos', path: '/orders' },
      { icon: Package, label: 'Gestão', path: '/delivery/management' },
    ]
  },
  { icon: ChefHat, label: 'KDS', path: '/kds', openInNewTab: true },
  { 
    icon: Package, 
    label: 'Produtos', 
    path: '/products',
    submenu: [
      { icon: Package, label: 'Lista de Produtos', path: '/products' },
      { icon: Tag, label: 'Categorias', path: '/categories' },
    ]
  },
  { icon: Users, label: 'Clientes', path: '/customers' },
  { icon: Warehouse, label: 'Estoque', path: '/stock' },
  {
    icon: DollarSign,
    label: 'Financeiro',
    path: '/finance',
    submenu: [
      { icon: DollarSign, label: 'Financeiro', path: '/finance' },
      { icon: DollarSign, label: 'Integrações · Cielo Gateway', path: '/integracoes/cielo-gateway' },
    ]
  },
  { 
    icon: FileText, 
    label: 'Fiscal', 
    path: '/fiscal',
    submenu: [
      { icon: FileText, label: 'Notas Fiscais', path: '/fiscal' },
      { icon: Building2, label: 'Integração SEFAZ', path: '/sefaz' },
    ]
  },
  { icon: Wallet, label: 'Caixa', path: '/cash' },
  { icon: BarChart3, label: 'Relatórios', path: '/reports' },
  { 
    icon: Bot, 
    label: 'BOT', 
    path: '/bot',
    submenu: [
      { icon: Shield, label: 'Monitor Handoff', path: '/whatsapp-monitor' },
      { icon: Inbox, label: 'Inbox WhatsApp', path: '/whatsapp-inbox' },
      { icon: MessageSquare, label: 'Templates WhatsApp', path: '/whatsapp-templates' },
      { icon: Activity, label: 'Monitoramento', path: '/monitoramento' },
    ]
  },
  { 
    icon: HeartHandshake, 
    label: 'CRM', 
    path: '/crm',
    submenu: [
      { icon: HeartHandshake, label: 'Visão Geral', path: '/crm' },
      { icon: MapPin, label: 'Mapa de Calor', path: '/heatmap' },
      { icon: PieChart, label: 'Analytics', path: '/analytics' },
    ]
  },
  { 
    icon: Banknote, 
    label: 'COD/Entregas', 
    path: '/driver-settlements',
    submenu: [
      { icon: Route, label: 'Roteirização', path: '/routing' },
      { icon: Bike, label: 'Motoboys', path: '/drivers' },
      { icon: Truck, label: 'Acertos', path: '/driver-settlements' },
      { icon: Receipt, label: 'Financeiro COD', path: '/motoboy-ledger' },
    ]
  },
  { 
    icon: Settings, 
    label: 'Configurações', 
    path: '/settings',
    submenu: [
      { icon: QrCode, label: 'Cardápio Digital', path: '/digital-menu' },
      { icon: Shield, label: 'Acessos', path: '/rbac' },
      { icon: Store, label: 'iFood', path: '/ifood' },
      { icon: Store, label: '99Food v4', path: '/99food-v4' },
      { icon: Store, label: 'Keeta', path: '/keeta' },
      { icon: Activity, label: 'Keeta Ops', path: '/keeta-ops' },
      { icon: Printer, label: 'Impressoras', path: '/printer-settings' },
    ]
  },
];

export function Sidebar() {
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState({});
  const { canAccessRoute } = usePermissions();
  
  const filteredMenuItems = useMemo(() => {
    const filterItems = (items) => {
      return items
        .map(item => ({...item}))
        .filter(item => {
          if (!canAccessRoute(item.path)) return false;
          if (item.submenu) {
            const filteredSubmenu = item.submenu.filter(sub => canAccessRoute(sub.path));
            if (filteredSubmenu.length === 0) return false;
            item.submenu = filteredSubmenu;
          }
          return true;
        });
    };
    return filterItems(menuItems);
  }, [canAccessRoute]);

  const toggleSubmenu = (label) => {
    setExpandedMenus(prev => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  const isSubmenuActive = (submenu) => {
    return submenu?.some(item => location.pathname === item.path);
  };

  const { open: mobileOpen, setOpen: setMobileOpen } = useContext(MobileMenuContext);

  return (
    <>
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setMobileOpen(false)} />
      )}
      <aside className={cn(
        "bg-white border-r border-slate-200 flex flex-col transition-all duration-300",
        "h-[100dvh] md:h-screen",
        "fixed md:sticky top-0 z-50",
        mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0",
        "w-64"
      )}>
        {/* Logo Section - Exatamente como no HTML de referência */}
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/logo.png" 
              alt="Hit Grill" 
              className="h-12 w-12 object-contain" 
            />
            <div>
              <h2 className="font-bold text-lg text-[#FF6B6B]" style={{ fontFamily: 'Barlow Condensed' }}>
                Hit Grill
              </h2>
              <p className="text-xs text-slate-500">Delivery e Retirada</p>
              <p className="text-[9px] text-slate-400 mt-0.5 font-mono" data-testid="build-badge" title="Build ID">
                build {process.env.REACT_APP_BUILD_ID || 'dev'}
              </p>
            </div>
          </div>
          <button
            onClick={() => setMobileOpen(false)}
            className="p-2 hover:bg-slate-100 rounded-md transition-colors md:hidden"
            data-testid="sidebar-toggle"
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-2 overflow-y-auto">
          <ul className="space-y-1">
            {filteredMenuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path || isSubmenuActive(item.submenu);
              const hasSubmenu = item.submenu && item.submenu.length > 0;
              const isExpanded = expandedMenus[item.label] || isSubmenuActive(item.submenu);

              if (hasSubmenu) {
                return (
                  <li key={item.label}>
                    <button
                      onClick={() => toggleSubmenu(item.label)}
                      data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={cn(
                        "flex items-center justify-between w-full px-3 py-2.5 rounded-md transition-all duration-200",
                        isActive
                          ? "bg-orange-50 text-[#FF6B6B] font-semibold"
                          : "text-slate-600 hover:bg-orange-50 hover:text-[#FF6B6B]"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <Icon size={20} />
                        <span className="text-sm">{item.label}</span>
                      </div>
                      <ChevronRight size={16} />
                    </button>
                    {isExpanded && (
                      <ul className="ml-4 mt-1 space-y-1 border-l-2 border-slate-200">
                        {item.submenu.map((subItem) => {
                          const SubIcon = subItem.icon;
                          const isSubActive = location.pathname === subItem.path;
                          return (
                            <li key={subItem.path}>
                              <Link
                                to={subItem.path}
                                onClick={() => setMobileOpen(false)}
                                className={cn(
                                  "flex items-center gap-2.5 px-3 py-2 rounded-md transition-all duration-200 ml-2",
                                  isSubActive
                                    ? "bg-orange-50 text-[#FF6B6B] font-semibold"
                                    : "text-slate-500 hover:bg-orange-50 hover:text-[#FF6B6B]"
                                )}
                              >
                                <SubIcon size={16} />
                                <span className="text-sm">{subItem.label}</span>
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              }

              return (
                <li key={item.path}>
                  {item.openInNewTab ? (
                    <a
                      href={item.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2.5 rounded-md transition-all duration-200",
                        isActive
                          ? "bg-orange-50 text-[#FF6B6B] font-semibold"
                          : "text-slate-600 hover:bg-orange-50 hover:text-[#FF6B6B]"
                      )}
                    >
                      <Icon size={20} />
                      <span className="text-sm">{item.label}</span>
                      <ExternalLink size={12} className="ml-auto opacity-50" />
                    </a>
                  ) : (
                    <Link
                      to={item.path}
                      onClick={() => setMobileOpen(false)}
                      data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2.5 rounded-md transition-all duration-200",
                        isActive
                          ? "bg-orange-50 text-[#FF6B6B] font-semibold"
                          : "text-slate-600 hover:bg-orange-50 hover:text-[#FF6B6B]"
                      )}
                    >
                      <Icon size={20} />
                      <span className="text-sm">{item.label}</span>
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>
      </aside>
    </>
  );
}

export function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [cashStatus, setCashStatus] = useState({ loading: true, isOpen: false, balance: 0 });
  const { setOpen: setMobileOpen } = useContext(MobileMenuContext);

  useEffect(() => {
    const checkCashStatus = async () => {
      try {
        const response = await api.get(`${API_URL}/api/cash/session/current`);
        setCashStatus({
          loading: false,
          isOpen: response.data.has_open_session,
          balance: response.data.session?.current_balance || 0
        });
      } catch (error) {
        setCashStatus({ loading: false, isOpen: false, balance: 0 });
      }
    };
    checkCashStatus();
    const interval = setInterval(checkCashStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  return (
    <header className="bg-gradient-to-r from-[#FF6B6B] to-[#FF7A5A] text-white shadow-md z-50 sticky top-0" data-testid="header">
      <div className="flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4">
        {/* Left side - Menu button + Logo + Title */}
        <div className="flex items-center gap-2 sm:gap-4">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 hover:bg-white/20 rounded-lg md:hidden"
            data-testid="mobile-menu-button"
          >
            <Menu className="w-6 h-6" />
          </button>
          <img 
            src="/logo.png" 
            alt="Hit Grill" 
            className="h-8 w-8 sm:h-10 sm:w-10 object-contain" 
          />
          <h1 className="text-lg sm:text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
            Hit Grill
          </h1>
        </div>

        {/* Right side - Cash status + User + Logout */}
        <div className="flex items-center gap-2 sm:gap-4">
          <Link
            to="/cash"
            data-testid="cash-status-indicator"
            className={cn(
              "flex items-center gap-2 px-3 py-2 rounded-lg transition-all",
              cashStatus.loading ? "bg-white/10" :
              cashStatus.isOpen ? "bg-green-500/20 hover:bg-green-500/30" : "bg-amber-500/20 hover:bg-amber-500/30"
            )}
          >
            {cashStatus.loading ? (
              <span className="text-sm text-white/70">Carregando...</span>
            ) : cashStatus.isOpen ? (
              <>
                <CheckCircle2 className="w-5 h-5 text-green-200" />
                <div className="text-left hidden sm:block">
                  <p className="text-xs text-green-200 font-medium">Caixa Aberto</p>
                  <p className="text-sm font-bold text-white" data-testid="cash-balance">{formatCurrency(cashStatus.balance)}</p>
                </div>
              </>
            ) : (
              <>
                <AlertCircle className="w-5 h-5 text-amber-200" />
                <div className="text-left hidden sm:block">
                  <p className="text-xs text-amber-200 font-medium">Caixa Fechado</p>
                  <p className="text-sm text-white/70">Clique para abrir</p>
                </div>
              </>
            )}
          </Link>

          <div className="text-right hidden sm:block">
            <p className="font-semibold text-sm">{user?.name || 'Administrador'}</p>
            <p className="text-xs text-orange-100">{user?.role || 'ADMIN_MASTER'}</p>
          </div>
          
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="bg-[#D62828] hover:bg-[#B91C1C] px-3 sm:px-4 py-2 rounded-md transition-colors text-sm font-medium"
          >
            Sair
          </button>
        </div>
      </div>
    </header>
  );
}

export function AccessDeniedPage() {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center">
        <Lock className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-slate-800 mb-2">Acesso Negado</h1>
        <p className="text-slate-600 mb-6">
          Você não tem permissão para acessar esta página.
        </p>
        <button
          onClick={() => navigate('/')}
          className="bg-[#FF6B6B] hover:bg-[#FF5252] text-white px-6 py-2 rounded-lg transition-colors"
        >
          Voltar ao Dashboard
        </button>
      </div>
    </div>
  );
}

export function MainLayout({ children }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <MobileMenuContext.Provider value={{ open: mobileMenuOpen, setOpen: setMobileMenuOpen }}>
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar />
        <div className="flex-1 flex flex-col min-w-0">
          <Header />
          <main className="flex-1 p-3 sm:p-6 md:p-8 overflow-x-hidden">{children}</main>
        </div>
      </div>
    </MobileMenuContext.Provider>
  );
}

export default MainLayout;
